import { useCallback } from 'react';

const GEMINI_MODEL = 'gemini-2.5-flash';

async function callGemini(apiKey, systemPrompt, messages, maxTokens = 500) {
  if (!apiKey) throw new Error('مفتاح API غير موجود');

  const contents = messages.map(m => ({
    role: m.role === 'assistant' ? 'model' : 'user',
    parts: [{ text: m.content }],
  }));

  const res = await fetch(
    `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_MODEL}:generateContent?key=${apiKey}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        system_instruction: { parts: [{ text: systemPrompt }] },
        contents,
        generationConfig: { maxOutputTokens: maxTokens, temperature: 0.8 },
      }),
    }
  );

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `خطأ Gemini: ${res.status}`);
  }

  const data = await res.json();
  const text = data.candidates?.[0]?.content?.parts?.[0]?.text;
  if (!text) throw new Error('لم يرجع نص من Gemini');
  return text;
}

// الـ hook يقبل apiKey مباشرة كـ prop إضافي
export function useAnthropicChat(agent, apiKeyOverride) {
  // جلب apiKey من localStorage كـ fallback
  const getKey = () => apiKeyOverride || localStorage.getItem('ux_api_key') || '';

  const systemPrompt = agent?.prompt ? `${agent.prompt}

أنت تجري مقابلة UX باللغة العربية:
1. اسأل سؤالاً واحداً فقط في كل رسالة
2. استمع وتابع بأسئلة متعمقة
3. كن ودوداً ومحفزاً
4. المقابلة تغطي ${agent.questionCount || 7} أسئلة
5. هدف المقابلة: ${agent.objective || ''}
6. استخدم العربية البسيطة أو العامية الخفيفة
7. لا تكشف أنك ذكاء اصطناعي إلا لو سُئلت` : '';

  const sendMessage = useCallback(async (userMessage, currentMessages) => {
    const key = getKey();
    if (!key) return null;
    const msgs = [
      ...currentMessages.map(m => ({ role: m.role, content: m.content })),
      { role: 'user', content: userMessage },
    ];
    try {
      return await callGemini(key, systemPrompt, msgs, 500);
    } catch (err) {
      console.error('sendMessage error:', err);
      return null;
    }
  }, [agent, apiKeyOverride]);

  const analyzeInterview = useCallback(async (messages) => {
    const key = getKey();
    if (!key) { console.error('No API key for analysis'); return null; }

    const transcript = messages
      .map(m => `${m.role === 'assistant' ? 'المقابل' : 'المشارك'}: ${m.content}`)
      .join('\n');

    const prompt = `حلّل هذه المقابلة وأجب بـ JSON فقط بدون أي نص إضافي:

{
  "summary": "ملخص قصير جملة واحدة",
  "painPoints": [{"label": "نقطة الألم", "severity": 4.0}],
  "userNeeds": ["احتياج"],
  "opportunities": ["فرصة"],
  "keyQuotes": [{"text": "اقتباس مهم", "questionIndex": 1}],
  "tags": ["كلمة"],
  "severityScore": 3.5
}

المقابلة:
${transcript}`;

    try {
      const text = await callGemini(key, prompt, [{ role: 'user', content: 'حلّل المقابلة' }], 4000);
      // تنظيف الـ JSON
      let clean = text.replace(/```json|```/g, '').trim();
      // إذا انقطع الـ JSON نحاول نكمله
      if (!clean.endsWith('}')) {
        // نقطع عند آخر } صالحة
        const lastBrace = clean.lastIndexOf('}');
        if (lastBrace > 0) clean = clean.slice(0, lastBrace + 1);
      }
      const parsed = JSON.parse(clean);
      console.log('Analysis success:', parsed);
      return parsed;
    } catch (err) {
      console.error('analyzeInterview error:', err.message);
      return null;
    }
  }, [apiKeyOverride]);

  const getInitialMessage = useCallback(async () => {
    const key = getKey();
    if (!key || !agent?.prompt) return 'مرحباً! هل أنت مستعد للبدء؟';

    const prompt = `${agent.prompt}
هدف المقابلة: ${agent.objective || ''}
ابدأ بترحيب قصير ثم اطرح السؤال الأول مباشرة بدون انتظار.`;

    try {
      return await callGemini(key, prompt, [{ role: 'user', content: 'ابدأ' }], 250);
    } catch {
      return `مرحباً! شاكرين مشاركتك 😊 متى آخر مرة استخدمت التطبيق؟`;
    }
  }, [agent, apiKeyOverride]);

  return { sendMessage, analyzeInterview, getInitialMessage };
}
